import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api, type AllianceTrend, type AllianceTrendMonth, type TrendMonths } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatGrowthPct } from '@/lib/killGrowth';
import { useAllianceLabel } from '@/lib/useCurrentAlliance';
import { downloadPoster, PosterCard, PosterFrame, PosterScaledPreview } from '@/components/Poster';
import { Loading, Panel, Segmented, fmtM } from './shared';

// Plan 055 — month-over-month development of the alliance.

type Metric = {
  key: string;
  title: string;
  sub: (topN: number) => string;
  value: (m: AllianceTrendMonth) => number | null;
  format: (v: number) => string;
  /** How many players the value is based on, if not all. */
  basis?: (m: AllianceTrendMonth) => string;
  color: string;
};

const METRICS: Metric[] = [
  {
    key: 'strength',
    title: 'Strength',
    sub: (n) => `Top ${n} M-Sum combined`,
    value: (m) => m.strength.topSumMSum,
    format: (v) => `${v.toFixed(0)} M`,
    basis: (m) => `median ${fmtM(m.strength.medianMSum, 1)} · ${m.strength.measured} measured`,
    color: '#22c55e',
  },
  {
    key: 'growth',
    title: 'Monthly growth',
    sub: () => 'Median M-Sum growth',
    value: (m) => m.growth.medianPct,
    format: (v) => formatGrowthPct(v),
    basis: (m) => `${m.growth.measured} measured`,
    color: '#38bdf8',
  },
  {
    key: 'kills',
    title: 'Kills',
    sub: (n) => `Top ${n} kills combined`,
    value: (m) => m.kills.topSumKillsM,
    format: (v) => `${v.toFixed(0)} M`,
    basis: (m) => `median ${fmtM(m.kills.medianKillsM, 1)} M · ${m.kills.measured} measured`,
    color: '#f43f5e',
  },
  {
    key: 'vs',
    title: 'VS',
    sub: () => 'Alliance total per week (avg)',
    value: (m) => m.vs.avgWeeklyTotalM,
    format: (v) => `${(v / 1000).toFixed(1)} B`,
    basis: (m) => (m.vs.weeks > 0 ? `${m.vs.won}W / ${m.vs.lost}L · ${m.vs.weeks} weeks` : 'no VS data'),
    color: '#f59e0b',
  },
  {
    key: 'members',
    title: 'Members',
    sub: () => 'Active players at month end',
    value: (m) => m.members,
    format: (v) => v.toFixed(0),
    color: '#a78bfa',
  },
];

function change(metric: Metric, months: AllianceTrendMonth[]) {
  const values = months.map(metric.value).filter((v): v is number => v != null);
  if (values.length < 2) return null;
  const [prev, last] = values.slice(-2);
  // Growth is already a percentage — compare in points, not relative.
  if (metric.key === 'growth') return { text: `${last - prev >= 0 ? '+' : ''}${(last - prev).toFixed(1)} pts`, up: last >= prev };
  if (prev === 0) return null;
  const pct = ((last - prev) / prev) * 100;
  return { text: `${pct >= 0 ? '+' : ''}${pct.toFixed(1)} %`, up: pct >= 0 };
}

export default function TrendTab() {
  const [months, setMonths] = useState<TrendMonths>(6);
  const q = useQuery({
    queryKey: ['insights', 'alliance-trend', months],
    queryFn: () => api.getAdminAllianceTrend(months),
  });
  const posterRef = useRef<HTMLDivElement | null>(null);
  const [busy, setBusy] = useState(false);

  if (q.isLoading || !q.data) return <Loading />;
  const data = q.data;

  const runExport = async () => {
    if (!posterRef.current) return;
    setBusy(true);
    try {
      await downloadPoster(posterRef.current, `alliance-trend-${months}m.png`);
    } catch (err) {
      console.error(err);
      alert('Export failed. Check console.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-400">
          Values at each month end (completed months). Strength and kills use each
          member's value at that date (interpolated, or the last report within 60 days).
        </p>
        <Segmented
          value={String(months) as '6' | '12'}
          onChange={(v) => setMonths(Number(v) as TrendMonths)}
          options={[
            { value: '6', label: '6 months' },
            { value: '12', label: '12 months' },
          ]}
        />
      </div>

      {data.months.length === 0 ? (
        <div className="rounded border border-slate-800 bg-slate-900/40 p-3 text-sm text-slate-400">
          No completed month with data yet.
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
          <PosterCard
            title="Poster"
            description="Strength, growth, kills, VS and members per month."
            busy={busy}
            onExport={runExport}
          >
            <PosterScaledPreview>
              <div ref={posterRef}>
                <TrendPoster data={data} />
              </div>
            </PosterScaledPreview>
          </PosterCard>
          <div className="grid gap-4 sm:grid-cols-2">
            {METRICS.map((metric) => (
              <MetricPanel key={metric.key} metric={metric} data={data} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricPanel({ metric, data }: { metric: Metric; data: AllianceTrend }) {
  const last = data.months[data.months.length - 1];
  const lastValue = metric.value(last);
  const delta = change(metric, data.months);
  const chartData = data.months.map((m) => ({ label: m.label.slice(0, 3), value: metric.value(m) }));
  return (
    <Panel title={metric.title}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-xl font-semibold tabular-nums">
          {lastValue == null ? '—' : metric.format(lastValue)}
        </div>
        {delta && (
          <div className={cn('text-xs tabular-nums', delta.up ? 'text-emerald-300' : 'text-amber-300')}>
            {delta.text} vs. prev. month
          </div>
        )}
      </div>
      <div className="text-[11px] text-slate-500">
        {metric.sub(data.topN)} · {last.label}
        {metric.basis && ` · ${metric.basis(last)}`}
      </div>
      <div className="mt-2 h-28">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#1e293b" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 10 }} tickLine={false} />
            <YAxis hide domain={metric.key === 'growth' ? [0, 'auto'] : ['auto', 'auto']} />
            <Tooltip
              cursor={{ fill: 'rgba(148,163,184,0.08)' }}
              content={({ payload }) => {
                const v = payload?.[0]?.value as number | null | undefined;
                return v == null ? null : (
                  <div className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs tabular-nums">
                    {metric.format(v)}
                  </div>
                );
              }}
            />
            <Bar dataKey="value" fill={metric.color} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
}

/** Poster: one row per metric, a bar per month — inline styles only. */
function TrendPoster({ data }: { data: AllianceTrend }) {
  const allianceLabel = useAllianceLabel();
  const first = data.months[0];
  const last = data.months[data.months.length - 1];
  return (
    <PosterFrame
      accent="#a78bfa"
      eyebrow="Alliance trend"
      title={`${allianceLabel} · Alliance trend`}
      weekLabel={first === last ? last.label : `${first.label} – ${last.label}`}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
        {METRICS.map((metric) => {
          const values = data.months.map(metric.value);
          const nums = values.filter((v): v is number => v != null);
          const max = Math.max(...nums, 0);
          // Bars start at 60 % of the minimum so month-to-month changes show.
          const floor = metric.key === 'growth' ? 0 : Math.min(...nums, max) * 0.6;
          const lastValue = metric.value(last);
          const delta = change(metric, data.months);
          return (
            <div
              key={metric.key}
              style={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: '260px 1fr',
                gap: 20,
                alignItems: 'stretch',
                borderRadius: 16,
                border: `2px solid ${metric.color}55`,
                background: `${metric.color}10`,
                padding: '12px 18px',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    color: metric.color,
                  }}
                >
                  {metric.title}
                </div>
                <div style={{ fontSize: 34, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                  {lastValue == null ? '—' : metric.format(lastValue)}
                </div>
                <div style={{ fontSize: 13, color: '#94a3b8' }}>{metric.sub(data.topN)}</div>
                {delta && (
                  <div style={{ fontSize: 14, color: delta.up ? '#6ee7b7' : '#fcd34d' }}>
                    {delta.text} vs. prev. month
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                {data.months.map((m, i) => {
                  const v = values[i];
                  const h = v == null || max === floor ? 0 : ((v - floor) / (max - floor)) * 100;
                  return (
                    <div
                      key={m.month}
                      style={{
                        flex: 1,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <div style={{ fontSize: 12, color: '#cbd5e1', fontVariantNumeric: 'tabular-nums' }}>
                        {v == null ? '' : metric.format(v)}
                      </div>
                      <div
                        style={{
                          width: '100%',
                          height: `${Math.max(h, v == null ? 0 : 4)}%`,
                          maxHeight: '70%',
                          background: metric.color,
                          borderRadius: 6,
                          opacity: i === data.months.length - 1 ? 1 : 0.6,
                        }}
                      />
                      <div style={{ fontSize: 12, color: '#94a3b8' }}>{m.label.slice(0, 3)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </PosterFrame>
  );
}
